// The design lab: every UI decision as a picker, applied to the real site.
//
// Choices are written as `data-lab-*` attributes and CSS variables on <html>
// and kept in localStorage, so a choice made on /design-lab follows you
// around the site until you reset it. The CSS that reads the attributes is
// in scss/myThemes/_designLab.scss; a few things the SVG charts and the dot
// background read here in JS.

export const STORAGE_KEY = "designLab";
export const CHANGE_EVENT = "designlab:change";

export const ACCENTS = {
  forest: { deep: "#0c4422", main: "#0f5a2e", soft: "#7fc79a" },
  moss: { deep: "#1b5e3a", main: "#2a7a4d", soft: "#9ad4ae" },
  teal: { deep: "#0b4f4a", main: "#127a72", soft: "#7fd0c8" },
  navy: { deep: "#12304f", main: "#1f4e79", soft: "#8fb8e0" },
  plum: { deep: "#3f1f3a", main: "#5e2b4f", soft: "#c893b8" },
  ink: { deep: "#1f2937", main: "#374151", soft: "#9ca3af" },
};

export const WIDTHS = {
  full: "2000px",
  wide: "1400px",
  medium: "1200px",
  narrow: "1040px",
};

/** Every decision, its options, and the default the site ships with. */
export const DECISIONS = [
  {
    key: "style",
    label: "Style",
    note: "The school the whole site sits in. Glass is what ships today.",
    options: [
      { value: "glass", label: "Glass" },
      { value: "neumorphic", label: "Neumorphic" },
      { value: "flat", label: "Flat" },
      { value: "editorial", label: "Editorial" },
    ],
    initial: "glass",
  },
  {
    key: "accent",
    label: "Accent",
    note: "Header, buttons, headline figures. Chart colours stay as graphFill.js.",
    options: Object.keys(ACCENTS).map((k) => ({ value: k, label: k })),
    initial: "forest",
  },
  {
    key: "radius",
    label: "Corners",
    options: [
      { value: "6", label: "6px" },
      { value: "10", label: "10px" },
      { value: "14", label: "14px" },
      { value: "20", label: "20px" },
    ],
    initial: "14",
  },
  {
    key: "surface",
    label: "Panels",
    note: "Glass is see-through with a blur over the dots.",
    options: [
      { value: "glass", label: "Glass" },
      { value: "paper", label: "Paper" },
      { value: "tint", label: "Tint" },
    ],
    initial: "glass",
  },
  {
    key: "shadows",
    label: "Shadows",
    options: [
      { value: "soft", label: "Soft" },
      { value: "flat", label: "Flat" },
    ],
    initial: "soft",
  },
  {
    key: "width",
    label: "Page width",
    options: Object.keys(WIDTHS).map((k) => ({ value: k, label: k })),
    initial: "full",
  },
  {
    key: "density",
    label: "Spacing",
    options: [
      { value: "comfortable", label: "Comfortable" },
      { value: "compact", label: "Compact" },
    ],
    initial: "comfortable",
  },
  {
    key: "type",
    label: "Type size",
    options: [
      { value: "small", label: "Small" },
      { value: "regular", label: "Regular" },
      { value: "large", label: "Large" },
    ],
    initial: "regular",
  },
  {
    key: "buttons",
    label: "Buttons",
    options: [
      { value: "compact", label: "Compact" },
      { value: "regular", label: "Regular" },
      { value: "large", label: "Large" },
    ],
    initial: "regular",
  },
  {
    key: "figures",
    label: "Figures",
    note: "Numbers inside result boxes.",
    options: [
      { value: "type", label: "Text" },
      { value: "pills", label: "Pills" },
    ],
    initial: "type",
  },
  {
    key: "charts",
    label: "Chart fill",
    options: [
      { value: "fill", label: "Area" },
      { value: "lines", label: "Lines" },
    ],
    initial: "fill",
  },
  {
    key: "grid",
    label: "Chart grid",
    options: [
      { value: "on", label: "On" },
      { value: "off", label: "Off" },
    ],
    initial: "on",
  },
  {
    key: "dots",
    label: "Dot background",
    options: [
      { value: "off", label: "Off" },
      { value: "subtle", label: "Subtle" },
      { value: "visible", label: "Visible" },
    ],
    initial: "subtle",
  },
  {
    key: "ads",
    label: "Ad units",
    note: "Which slots show. Mid sits under results, end sits after the page.",
    options: [
      { value: "both", label: "Mid + end" },
      { value: "end", label: "End only" },
      { value: "none", label: "None" },
    ],
    initial: "both",
  },
  {
    key: "motion",
    label: "Motion",
    options: [
      { value: "on", label: "On" },
      { value: "off", label: "Off" },
    ],
    initial: "on",
  },
];

export const defaults = () =>
  DECISIONS.reduce((memo, d) => ({ ...memo, [d.key]: d.initial }), {});

const isValid = (key, value) => {
  const decision = DECISIONS.find((d) => d.key === key);
  return !!decision && decision.options.some((o) => o.value === value);
};

/** Saved choices merged over the defaults; bad values are dropped. */
export const loadLab = () => {
  const base = defaults();
  if (typeof window === "undefined") return base;
  try {
    const saved = JSON.parse(window.localStorage.getItem(STORAGE_KEY) || "{}");
    Object.keys(saved).forEach((key) => {
      if (isValid(key, saved[key])) base[key] = saved[key];
    });
  } catch (e) {
    // A broken or blocked localStorage just means defaults.
  }
  return base;
};

/** True when any choice differs from the shipped default. */
export const isCustomised = (settings) => {
  const base = defaults();
  return Object.keys(base).some((key) => settings[key] !== base[key]);
};

const dataKey = (key) => `lab${key.charAt(0).toUpperCase()}${key.slice(1)}`;

// The prerender script runs with a bare `document` shim and no <html>
// element, so every read of the root is guarded.
const rootElement = () =>
  (typeof document !== "undefined" && document.documentElement) || null;

/** Write the choices onto <html> so the site's CSS and charts pick them up. */
export const applyLab = (settings) => {
  const root = rootElement();
  if (!root || !root.dataset || !root.style) return;
  const custom = isCustomised(settings);
  DECISIONS.forEach((d) => {
    if (custom) {
      root.dataset[dataKey(d.key)] = settings[d.key];
    } else {
      delete root.dataset[dataKey(d.key)];
    }
  });
  const accent = ACCENTS[settings.accent] || ACCENTS.forest;
  const vars = {
    "--lab-accent-deep": accent.deep,
    "--lab-accent": accent.main,
    "--lab-accent-soft": accent.soft,
    "--lab-radius": `${settings.radius}px`,
    "--lab-width": WIDTHS[settings.width] || WIDTHS.full,
  };
  Object.keys(vars).forEach((name) => {
    if (custom) root.style.setProperty(name, vars[name]);
    else root.style.removeProperty(name);
  });
  window.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: settings }));
};

export const saveLab = (settings) => {
  if (typeof window === "undefined") return;
  try {
    if (isCustomised(settings)) {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
    } else {
      window.localStorage.removeItem(STORAGE_KEY);
    }
  } catch (e) {
    // Nothing to do: the choice still applies for this page view.
  }
};

/** Read one choice off <html> from anywhere (charts, dots). */
export const labValue = (key) => {
  const root = rootElement();
  if (!root || !root.dataset) return undefined;
  return root.dataset[dataKey(key)];
};

/** Apply whatever was saved. Called once when the app boots. */
export const bootLab = () => {
  const settings = loadLab();
  if (isCustomised(settings)) applyLab(settings);
  return settings;
};
